<script setup lang="ts">
  import { mdiAlertOutline, mdiCheckAll, mdiCreation, mdiProgressUpload, mdiTimelineClockOutline } from '@mdi/js'
  import { computed } from 'vue'
  import { useStore } from '@/store'
  import { useI18n } from 'vue-i18n'
  import MdiIcon from '@/common/components/svg/MdiIcon.vue'
  import HeaderBarButton from '@/common/components/headerbar/HeaderBarButton.vue'
  import { differenceInSeconds, useTime } from '@/common/services/time'
  import { useRoute } from 'vue-router'

  const time = useTime()
  const store = useStore()
  const i18n = useI18n()
  const route = useRoute()

  const syncJobCount = computed(() => store.state.app.getsHTTPSyncJobs?.length ?? 0)
  const needsCommit = computed(() => store.getters['app/commitPending'])
  const lastGetsCommit = computed(() => store.state.app.lastGetsCommit)
  const systems = computed(() => store.getters['app/availableSystems'])
  const isSyncRoute = computed(() => route.name === 'sync')
  const linkTo = computed(() => systems.value.length === 0 ? { name: 'systemParamList' } : { name: 'sync' })

  const syncTooltip = computed(() => {
    if (systems.value.length === 0) {
      return i18n.t('systemparam.states.missing')
    }

    if (lastGetsCommit.value.getTime() === 0 && !store.state.app.features.demo_mode) {
      return i18n.t('changelog.states.never_synced')
    }

    if (syncJobCount.value > 0) {
      return i18n.t('changelog.states.submitting_changes', { count: syncJobCount.value })
    }

    return commitPendingSinceSeconds.value !== null
      ? i18n.t('changelog.states.needs_commit')
      : i18n.t('changelog.states.in_sync')
  })

  const icon = computed(() => {
    switch (true) {
    case store.state.app.features.demo_mode:
      return mdiCheckAll
    case systems.value.length === 0:
      return mdiAlertOutline
    case syncJobCount.value > 0:
      return mdiProgressUpload
    case lastGetsCommit.value.getTime() === 0:
      return mdiCreation
    case needsCommit.value:
      return mdiTimelineClockOutline
    default:
      return mdiCheckAll
    }
  })

  const commitPendingSinceSeconds = computed(() => {
    if (!needsCommit.value || syncJobCount.value > 0) {
      return null
    }

    const lastChange = store.state.app.lastGetsChange
    if (!lastChange) {
      return null
    }

    return Math.max(0, differenceInSeconds(time.value.now, lastChange))
  })

  const signalPendingCommit = computed(() => {
    if (systems.value.length === 0) {
      return true
    }

    if (commitPendingSinceSeconds.value === null) {
      return false
    }

    return true
  })

  const maxHighlightAfterSeconds = 40
  const highlightPercentage = computed(() => {
    if (systems.value.length === 0) {
      return 100
    }

    if (!needsCommit.value || !commitPendingSinceSeconds.value) {
      return 0
    }

    return Math.min(100, Math.round(commitPendingSinceSeconds.value / maxHighlightAfterSeconds * 100))
  })
</script>

<template>
  <RouterLink
    :to="linkTo"
    :class="{'pointer-events-none': syncJobCount > 0}"
    class="relative leading-none inline-flex mx-auto flex-shrink-0 rounded-full p-1 text-csBlue-200 hover:text-white focus:outline-none"
  >
    <HeaderBarButton
      v-tooltip.bottom="{
        content: () => $t('changelog.go_to_sync'),
        delay: { show: 1500 },
        disabled: syncJobCount > 0
      }"
      :highlight="signalPendingCommit && !isSyncRoute"
      :highlightPercentage="highlightPercentage"
    >
      <template #left>
        <MdiIcon :icon="icon" :class="icon === mdiTimelineClockOutline ? 'size-5' : 'size-[1.15rem]'" />
      </template>
      {{ syncTooltip }}
    </HeaderBarButton>
  </RouterLink>
</template>